import Vue from "vue";
import examApi from "../service/exam-api";

const vm = Vue.prototype;

// Monotonic counter shared by all FETCH_SESSIONS calls. Every filter/pagination/search change fires a
// new request; when the server is slow they overlap and can resolve out of order. Without this guard an
// earlier-issued but later-arriving response would overwrite the newest one (wrong tab/page shown, the
// "filters stop working / pagination corrupts data" bug). We commit only the latest request's response.
let sessionsRequestSeq = 0;

export const state = () => ({
  sessions: [],
  totalElements: 0,
  // Real totals for the tab cards under the current filters (from /summary), not the current page.
  summary: { total: 0, passed: 0, failed: 0, inProgress: 0 },
  currentDetail: null,
  faculties: [],
  educationTypes: [],
  educationLanguages: [],
});

export const mutations = {
  SET_SESSIONS(state, payload) {
    state.sessions = payload.content || [];
    state.totalElements = payload.totalElements || 0;
  },
  SET_SUMMARY(state, payload) {
    state.summary = {
      total: Number(payload?.total || 0),
      passed: Number(payload?.passed || 0),
      failed: Number(payload?.failed || 0),
      inProgress: Number(payload?.inProgress || 0),
    };
  },
  SET_DETAIL(state, payload) {
    state.currentDetail = payload;
  },
  SET_FACULTIES(state, payload) {
    state.faculties = vm.$toArray(payload);
  },
  SET_EDUCATION_TYPES(state, payload) {
    state.educationTypes = vm.$toArray(payload);
  },
  SET_EDUCATION_LANGUAGES(state, payload) {
    state.educationLanguages = vm.$toArray(payload);
  },
};

export const actions = {
  async FETCH_SESSIONS({ commit }, { page = 0, size = 20, resultStatus, facultyId, educationTypeId, educationLanguageId, search } = {}) {
    const seq = ++sessionsRequestSeq;
    try {
      let query = `admin/exam/results?page=${page}&size=${size}`;
      // resultStatus = PASSED | FAILED | IN_PROGRESS (tab); null/undefined = Barchasi (all).
      if (resultStatus) query += `&resultStatus=${resultStatus}`;
      if (facultyId) query += `&facultyId=${facultyId}`;
      if (educationTypeId) query += `&educationTypeId=${educationTypeId}`;
      if (educationLanguageId) query += `&educationLanguageId=${educationLanguageId}`;
      if (search) query += `&search=${encodeURIComponent(search)}`;

      const { data } = await examApi.get(query);
      // A newer request was fired while this one was in flight: its response is what the UI must show,
      // so drop this stale one instead of clobbering the grid with an old page/filter.
      if (seq !== sessionsRequestSeq) return null;
      commit("SET_SESSIONS", data);
      return data;
    } catch (e) {
      if (seq === sessionsRequestSeq) console.error("FETCH_SESSIONS error:", e);
      return null;
    }
  },

  // Real totals for the tab cards under the current filters (independent of the page / resultStatus).
  async FETCH_SUMMARY({ commit }, { facultyId, educationTypeId, educationLanguageId, search } = {}) {
    try {
      let query = "admin/exam/results/summary?";
      const params = [];
      if (facultyId) params.push(`facultyId=${facultyId}`);
      if (educationTypeId) params.push(`educationTypeId=${educationTypeId}`);
      if (educationLanguageId) params.push(`educationLanguageId=${educationLanguageId}`);
      if (search) params.push(`search=${encodeURIComponent(search)}`);
      query += params.join("&");

      const { data } = await examApi.get(query);
      commit("SET_SUMMARY", data);
      return data;
    } catch (e) {
      console.error("FETCH_SUMMARY error:", e);
      return null;
    }
  },

  async FETCH_DETAIL({ commit }, { sessionId }) {
    try {
      const { data } = await examApi.get(`admin/exam/results/${sessionId}`);
      commit("SET_DETAIL", data);
      return data;
    } catch (e) {
      console.error("FETCH_DETAIL error:", e);
      return null;
    }
  },

  async UPDATE_SCORE(ctx, { sessionId, totalScore, passed }) {
    try {
      await examApi.put(`admin/exam/results/${sessionId}/score`, { totalScore, passed });
      return true;
    } catch (e) {
      console.error("UPDATE_SCORE error:", e);
      return false;
    }
  },

  async DELETE_SESSION(ctx, { sessionId }) {
    try {
      await examApi.delete(`admin/exam/results/${sessionId}`);
      return true;
    } catch (e) {
      console.error("DELETE_SESSION error:", e);
      return false;
    }
  },

  async EXPORT_EXCEL(ctx, { resultStatus, facultyId, educationTypeId, educationLanguageId, search } = {}) {
    try {
      let query = "admin/exam/results/export?";
      const params = [];
      if (resultStatus) params.push(`resultStatus=${resultStatus}`);
      if (facultyId) params.push(`facultyId=${facultyId}`);
      if (educationTypeId) params.push(`educationTypeId=${educationTypeId}`);
      if (educationLanguageId) params.push(`educationLanguageId=${educationLanguageId}`);
      if (search) params.push(`search=${encodeURIComponent(search)}`);
      query += params.join("&");

      const { data } = await examApi.get(query, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "exam_results.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      return true;
    } catch (e) {
      console.error("EXPORT_EXCEL error:", e);
      return false;
    }
  },

  async FETCH_FACULTIES({ commit }) {
    try {
      const { data } = await examApi.get("admin/exam/faculties");
      if (data) commit("SET_FACULTIES", data);
      return data;
    } catch (e) {
      return null;
    }
  },

  async FETCH_EDUCATION_TYPES({ commit }) {
    try {
      const { data } = await examApi.get("admin/exam/education-types");
      if (data) commit("SET_EDUCATION_TYPES", data);
      return data;
    } catch (e) {
      return null;
    }
  },

  async FETCH_EDUCATION_LANGUAGES({ commit }) {
    try {
      const { data } = await examApi.get("admin/exam/languages");
      if (data) commit("SET_EDUCATION_LANGUAGES", data);
      return data;
    } catch (e) {
      return null;
    }
  },
};

export const getters = {
  GET_SESSIONS: (state) => state.sessions,
  GET_TOTAL_ELEMENTS: (state) => state.totalElements,
  GET_SUMMARY: (state) => state.summary,
  GET_DETAIL: (state) => state.currentDetail,
  GET_FACULTIES: (state) => state.faculties,
  GET_EDUCATION_TYPES: (state) => state.educationTypes,
  GET_EDUCATION_LANGUAGES: (state) => state.educationLanguages,
};
